/**
 * scoreboard.ts — the Tab scoreboard and the round banner.
 *
 * Two things reading the same server state (`RoundMsg`): a table of wins, kills
 * and deaths, and a line across the middle of the screen saying where the match
 * is. The table is held open by Tab, and comes up on its own at the end of a
 * round — the moment you most want to see it is the moment you just lost, and
 * reaching for a key then is not what anyone does.
 *
 * Nothing here decides anything. The phase, the round number, the winner and the
 * scores are all the server's (AGENTS.md: server-authoritative); the only local work is
 * counting the intermission down, from the duration the server sent, on the
 * client's own clock — the alternative is a message per tick for a number the
 * player can watch tick down for themselves.
 */

import {
  NO_WINNER,
  compareScores,
  type PlayerInfo,
  type RoundMsg,
  type ScoreEntry,
} from '@dueler/shared';

/** How long "ROUND n — FIGHT" stays up after a round starts, ms. */
const FIGHT_FLASH_MS = 1600;

export interface Scoreboard {
  /** Latest round state from the server. `now` stamps the countdown's start. */
  setRound(msg: RoundMsg, now: number): void;
  /** Tab: hold the table open. Does nothing to the round banner. */
  toggle(): void;
  /** Re-renders the banner and countdown. Call once per frame. */
  update(now: number, players: Map<number, PlayerInfo>, selfSlot: number): void;
  dispose(): void;
}

export function createScoreboard(): Scoreboard {
  const root = document.createElement('div');
  root.className = 'score-layer';
  root.innerHTML = `
    <div class="round-banner" id="banner" hidden>
      <div class="round-banner-line" id="banner-line"></div>
      <div class="round-banner-sub" id="banner-sub"></div>
    </div>
    <div class="scoreboard" id="board" hidden>
      <div class="score-title" id="board-title"></div>
      <table class="score-table">
        <thead>
          <tr><th class="score-name">duelist</th><th>wins</th><th>kills</th><th>deaths</th></tr>
        </thead>
        <tbody id="board-rows"></tbody>
      </table>
      <div class="score-hint">Tab to close</div>
    </div>
  `;
  document.body.appendChild(root);

  const banner = root.querySelector<HTMLDivElement>('#banner')!;
  const bannerLine = root.querySelector<HTMLDivElement>('#banner-line')!;
  const bannerSub = root.querySelector<HTMLDivElement>('#banner-sub')!;
  const board = root.querySelector<HTMLDivElement>('#board')!;
  const boardTitle = root.querySelector<HTMLDivElement>('#board-title')!;
  const boardRows = root.querySelector<HTMLElement>('#board-rows')!;

  let round: RoundMsg | undefined;
  /** performance.now() the next round is due, from the duration the server sent. */
  let deadline = 0;
  /** When the current round started, for the FIGHT flash. */
  let startedAt = 0;
  let held = false;

  const nameOf = (players: Map<number, PlayerInfo>, slot: number): string =>
    players.get(slot)?.name ?? `slot ${slot}`;

  function renderRows(players: Map<number, PlayerInfo>, selfSlot: number): void {
    // The roster is the client's mirror of who is connected; the scores are the
    // server's. Joining between round messages means a row of zeroes, never a
    // missing player.
    const scores = new Map<number, ScoreEntry>(round?.scores.map((s) => [s.slot, s]) ?? []);
    const rows: ScoreEntry[] = [...players.keys()]
      .map((slot) => scores.get(slot) ?? { slot, wins: 0, kills: 0, deaths: 0 })
      .sort(compareScores);

    boardRows.replaceChildren(
      ...rows.map((entry) => {
        const tr = document.createElement('tr');
        if (entry.slot === selfSlot) tr.className = 'you';
        for (const [index, text] of [
          nameOf(players, entry.slot),
          `${entry.wins}`,
          `${entry.kills}`,
          `${entry.deaths}`,
        ].entries()) {
          const td = document.createElement('td');
          // textContent, not innerHTML: names are untrusted input.
          td.textContent = text;
          if (index === 0) td.className = 'score-name';
          tr.appendChild(td);
        }
        return tr;
      }),
    );
  }

  return {
    setRound(msg, now) {
      const started = msg.phase === 'fighting' && msg.round !== round?.round;
      round = msg;
      deadline = now + msg.nextInMs;
      if (started) startedAt = now;
    },

    toggle() {
      held = !held;
    },

    update(now, players, selfSlot) {
      const phase = round?.phase ?? 'warmup';
      const decided = phase === 'intermission';

      // Open on Tab, and by itself while the round is being scored.
      const showBoard = held || decided;
      board.hidden = !showBoard;
      if (showBoard) {
        boardTitle.textContent =
          round === undefined || round.round === 0 ? 'WARMUP' : `ROUND ${round.round}`;
        renderRows(players, selfSlot);
      }

      if (phase === 'warmup') {
        banner.hidden = false;
        bannerLine.textContent = 'WARMUP';
        bannerSub.textContent =
          players.size >= 2
            ? 'sparring Dummy — block, parry, fight back. Deaths respawn.'
            : 'waiting for another duelist — respawning freely';
        return;
      }

      if (decided) {
        const winner = round?.winner ?? NO_WINNER;
        banner.hidden = false;
        bannerLine.textContent =
          winner === NO_WINNER
            ? 'DOUBLE KILL'
            : winner === selfSlot
              ? 'ROUND WON'
              : `${nameOf(players, winner)} WINS THE ROUND`;
        const seconds = Math.max(0, Math.ceil((deadline - now) / 1000));
        bannerSub.textContent = `next round in ${seconds}`;
        return;
      }

      // Fighting: a brief callout that the round is live, then out of the way.
      const flashing = now - startedAt < FIGHT_FLASH_MS;
      banner.hidden = !flashing;
      if (flashing) {
        bannerLine.textContent = `ROUND ${round?.round ?? 1}`;
        bannerSub.textContent = 'FIGHT';
        // Fade the callout out rather than cutting it, so it reads as leaving.
        banner.style.opacity = `${1 - (now - startedAt) / FIGHT_FLASH_MS}`;
      } else {
        banner.style.opacity = '1';
      }
    },

    dispose: () => root.remove(),
  };
}
